/**
 * D98.3 - the one issues bar above the Lines grid. Presentational: all facts come from lineIssues.ts.
 *
 * Props CIP passes:
 *   result            analyseInvoice(...) output
 *   dismissedKeys     Set of alertKeys already dismissed (from invoice.alertHistory); those are hidden
 *   status            invoice status: once sent there is nothing left to send, so blockers and their heading go; cancelled shows nothing
 *   readOnly          cancelled invoice or no edit permission: the Dismiss buttons are hidden
 *   onFilterIssues(lineIndexes)  grid shows only these rows; called with [] to clear (toggle)
 *   onDismiss(alertKey)          the page's existing dismiss flow (reason form + server call)
 */
import React, { useState } from 'react';
import type { InvoiceIssues, IssueCode, LineIssue } from './lineIssues';

const BLOCK_LABEL: Partial<Record<IssueCode, string>> = {
  unpriced: 'unpriced',
  missing_name: 'without an item name',
  qty_needed: 'needing a quantity',
  supplier_not_picked: 'with the supplier not picked',
};

const SENT_ONWARD = new Set(['sent', 'partially_paid', 'paid']);

const lineList = (nos: number[]) => `${nos.length === 1 ? 'line' : 'lines'} ${nos.join(', ')}`;

export function IssuesBar({
  result,
  dismissedKeys,
  status,
  readOnly = false,
  onFilterIssues,
  onDismiss,
}: {
  result: InvoiceIssues;
  dismissedKeys?: ReadonlySet<string>;
  status?: string;
  readOnly?: boolean;
  onFilterIssues?: (lineIndexes: number[]) => void;
  onDismiss?: (alertKey: string) => void;
}) {
  const [filtering, setFiltering] = useState(false);
  const afterSend = status !== undefined && SENT_ONWARD.has(status);
  const hidden = (i: LineIssue) => !!i.alertKey && !!dismissedKeys?.has(i.alertKey);

  const blocks = new Map<IssueCode, number[]>();
  const warns: Array<{ issue: LineIssue; index?: number }> = [];
  let notSupplied = 0;
  const flagged: number[] = [];
  result.lines.forEach((issues, index) => {
    let touched = false;
    for (const i of issues.filter((x) => !hidden(x))) {
      if (i.severity === 'block') {
        if (afterSend && i.code !== 'supplier_not_picked') continue;
        blocks.set(i.code, [...(blocks.get(i.code) || []), result.lineNumbers[index]]);
        touched = true;
      } else if (i.severity === 'warn') {
        warns.push({ issue: i, index });
        touched = true;
      } else if (i.code === 'not_supplied') notSupplied += 1;
    }
    if (touched) flagged.push(index);
  });
  result.invoice.filter((i) => !hidden(i)).forEach((issue) => warns.push({ issue }));

  if (status === 'cancelled') return null;
  const blockCount = Array.from(blocks.values()).reduce((sum, nos) => sum + nos.length, 0);
  if (!blockCount && !warns.length && !notSupplied) return null;

  const toggle = () => {
    const next = !filtering;
    setFiltering(next);
    onFilterIssues?.(next ? flagged : []);
  };

  return (
    <div className={`rounded border border-slate-200 border-l-4 ${afterSend ? 'border-l-amber-400' : 'border-l-rose-500'} bg-white px-3 py-2 text-sm`} role="region" aria-label="Issues">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        {blockCount > 0 && !afterSend && <b className="text-rose-700">{blockCount} to fix before sending</b>}
        {afterSend && (warns.length > 0 || blockCount > 0) && <b className="text-amber-800">Needs a look</b>}
        {Array.from(blocks.entries()).map(([code, nos]) => (
          <span key={code}>
            {nos.length} {BLOCK_LABEL[code] || code} ({lineList(nos)})
          </span>
        ))}
        {notSupplied > 0 && <span className="text-slate-600">{notSupplied} not supplied</span>}
        {flagged.length > 0 && onFilterIssues && (
          <button type="button" className="ml-auto text-xs text-primary-600 underline" onClick={toggle}>
            {filtering ? 'Show all lines' : 'Show only these'}
          </button>
        )}
      </div>
      {warns.length > 0 && (
        <ul className="mt-1 space-y-1">
          {warns.map(({ issue }, k) => (
            <li key={`${issue.code}-${k}`} className="flex items-center gap-2 text-amber-800">
              <span>{issue.message}</span>
              {issue.alertKey && !readOnly && onDismiss && (
                <button type="button" className="text-xs text-slate-600 underline" onClick={() => onDismiss(issue.alertKey!)}>
                  Dismiss
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default IssuesBar;
